import {
  faCalendarAlt,
  faClipboardList,
  faClock,
  faGlobe,
  faMapMarkerAlt,
  faPaperclip,
  faTag,
  faTags,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, Interval } from 'luxon'
import React, { Fragment } from 'react'
import { Attachment, Currency, ExploreEvent, ExploreProgram } from '@citruscamps/citrus-client'
import { Address } from '../../../utils/address'
import { getSafeBrandColor } from '../../../utils/branding-utils'
import { formatEventType, pluralize } from '../../../utils/formatters'
import { TruncatedText } from '../../TruncatedText/TruncatedText'

const parseDate = (date: string | Date, timezone?: string): DateTime | undefined =>
  date && date instanceof Date
    ? DateTime.fromJSDate(date, {
        zone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    : date && typeof date === 'string'
    ? DateTime.fromJSDate(new Date(date), {
        zone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    : undefined

interface IProps {
  event: ExploreEvent
  program: ExploreProgram
  attachments?: Attachment[]
  onAddCalendar?: () => void
}

export const EventSummary = ({
  event,
  program,
  attachments = [],
  onAddCalendar: handleAddCalendar,
}: IProps) => {
  const starts_at: DateTime | undefined = parseDate(event?.starts_at, event?.timezone)
  const ends_at: DateTime | undefined = parseDate(event?.ends_at, event?.timezone)
  const date_range: string = [
    starts_at?.toFormat('LLL d'),
    starts_at && !ends_at?.hasSame(starts_at, 'year') && starts_at?.toFormat(', yyyy'),
    starts_at && !ends_at?.hasSame(starts_at, 'day') && ' - ',
    starts_at && (!ends_at?.hasSame(starts_at, 'year') || !ends_at?.hasSame(starts_at, 'month'))
      ? ends_at?.toFormat('DD')
      : starts_at && !ends_at?.hasSame(starts_at, 'day')
      ? ends_at?.toFormat('d, yyyy')
      : ends_at?.toFormat(', yyyy'),
  ]
    .filter((d) => !!d)
    .join('')
  const time_range: string = [
    starts_at ? starts_at.toFormat('t') : '',
    ends_at ? ends_at.toFormat('t ZZZZ') : '',
  ]
    .filter((d) => !!d)
    .join(' - ')
  let event_interval: Interval | undefined =
    starts_at && ends_at ? Interval.fromDateTimes(starts_at, ends_at) : undefined
  let event_days: number = event_interval ? Math.ceil(event_interval.toDuration('days').days) : 0
  let event_hours: number = event_interval ? Math.ceil(event_interval.toDuration('hours').hours) : 0
  const event_type: string = formatEventType(program).toLowerCase()
  let event_duration: string =
    event_days > 0
      ? `${event_days} day${pluralize(event_days)} of ${event_type}`
      : `${event_hours} hour${pluralize(event_hours)} of ${event_type}`
  const lowestOption: Currency | undefined = event?.order_availability.minimum_option_price
  const highestOption: Currency | undefined = event?.order_availability.maximum_option_price
  const price_range: string =
    lowestOption && highestOption && lowestOption?.value === highestOption.value
      ? lowestOption.value === 0
        ? event?.order_availability?.available_spots > 0
          ? 'Free'
          : ''
        : lowestOption.display
      : [
          lowestOption && lowestOption.display
            ? (lowestOption.display.split('.').shift() || '').trim()
            : undefined,
          highestOption && highestOption.display
            ? (highestOption.display.split('.').shift() || '').trim()
            : undefined,
        ]
          .filter((d) => !!d)
          .join(' · ')

  const place_name: React.ReactElement[] | string = event?.online_event
    ? 'Online Event'
    : event.venues && event.venues?.length > 0
    ? event.venues.map((venue) => (
        <Fragment key={`venue-link-${venue.id}`}>
          <a
            href={`https://maps.google.com/?q=${Address.fromObject(venue).toLocaleString(
              Address.PLACENAME,
            )}&ll=${venue.geometry?.coordinates[0]},${venue.geometry?.coordinates[1]}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dark mb-2"
          >
            {Address.fromObject(venue)
              .toLocaleString(Address.MAILING_ADDRESS)
              .split('\n')
              .map((line: string, k: number) => (
                <div key={['address-line', venue?.id, k].join(':')}>{line}</div>
              ))}
          </a>
          <br />
        </Fragment>
      ))
    : 'To be announced'
  const hasMultiplePrices: boolean =
    !!event?.order_availability.minimum_option_price &&
    !!event?.order_availability.maximum_option_price &&
    event?.order_availability.minimum_option_price?.value !==
      event?.order_availability.maximum_option_price?.value
  return (
    <>
      {program?.website ? (
        <div className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={faGlobe}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-8 col-md-10">
            <a
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={program.website.includes('//') ? program.website : `//${program.website}`}
            >
              Visit website
            </a>
          </div>
        </div>
      ) : null}
      {event.is_series ? (
        <div className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={faCalendarAlt}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-10">
            {event.series_dates?.map((date, i, arr) => {
              const firstIndexOfYear: number = arr.findIndex(
                (d) =>
                  DateTime.fromJSDate(d.starts_at).year ===
                  DateTime.fromJSDate(date.starts_at).year,
              )
              const isFirstOfYear: boolean = i === firstIndexOfYear
              const hasMoreThanOneYear: boolean =
                arr.length > 1 &&
                arr
                  .map((d) => DateTime.fromJSDate(d.starts_at).year)
                  .reduce<number[]>((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]), [])
                  .length > 1

              return (
                <Fragment key={date.id}>
                  {isFirstOfYear && hasMoreThanOneYear && (
                    <h6 className="mt-2">{DateTime.fromJSDate(date.starts_at).year}</h6>
                  )}
                  <TruncatedText className="mb-2 mb-sm-0">
                    {Interval.fromDateTimes(
                      DateTime.fromJSDate(date.starts_at),
                      DateTime.fromJSDate(date.ends_at),
                    ).toLocaleString({
                      ...DateTime.DATE_MED,
                      year: undefined,
                    })}
                    <br className="d-block d-sm-none" />
                    <span className="d-none d-sm-inline"> · </span>
                    {Interval.fromDateTimes(
                      DateTime.fromJSDate(date.starts_at),
                      DateTime.fromJSDate(date.ends_at),
                    ).toFormat('t')}
                    {DateTime.fromJSDate(date.starts_at).toFormat(' ZZZZ')}
                  </TruncatedText>
                </Fragment>
              )
            })}
            {handleAddCalendar && (
              <div>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => handleAddCalendar()}
                >
                  Add to Calendar
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {date_range ? (
            <div className="row no-gutters my-3">
              <div className="col-2">
                <FontAwesomeIcon
                  size="lg"
                  fixedWidth={true}
                  icon={faCalendarAlt}
                  color={getSafeBrandColor(program?.preferences)}
                />
              </div>
              <div className="col-8 col-md-10">
                <div>{date_range}</div>
                {handleAddCalendar && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => handleAddCalendar()}
                    >
                      Add to Calendar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
          {time_range && event_duration ? (
            <div className="row no-gutters my-3">
              <div className="col-2">
                <FontAwesomeIcon
                  size="lg"
                  fixedWidth={true}
                  icon={faClock}
                  color={getSafeBrandColor(program?.preferences)}
                />
              </div>
              <div className="col-8 col-md-10">
                <div>{time_range}</div>
                <div>{event_duration}</div>
              </div>
            </div>
          ) : null}
        </>
      )}
      {price_range ? (
        <div className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={hasMultiplePrices ? faTags : faTag}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-8 col-md-10">{price_range}</div>
        </div>
      ) : null}
      {event.order_availability.available_spots ? (
        <div className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={faClipboardList}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-8 col-md-10">
            {event.order_availability.available_spots} spot
            {event.order_availability.available_spots !== 1 ? 's' : ''} available
          </div>
        </div>
      ) : null}
      {place_name ? (
        <div className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={event.online_event ? faGlobe : faMapMarkerAlt}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-8 col-md-10">{place_name || ''}</div>
        </div>
      ) : null}
      {attachments.map((attachment) => (
        <div key={attachment.id} className="row no-gutters my-3">
          <div className="col-2">
            <FontAwesomeIcon
              size="lg"
              fixedWidth={true}
              icon={faPaperclip}
              color={getSafeBrandColor(program?.preferences)}
            />
          </div>
          <div className="col-8 col-md-10">
            <a target="_blank" rel="noopener noreferrer" href={attachment.file.url}>
              {attachment.file.filename}
            </a>
          </div>
        </div>
      ))}
    </>
  )
}
